import React from 'react';
import { Box, Chip } from '@mui/material';
import styles from './ComingSoonTag.module.css';

interface ComingSoonTagProps {
  variant?: 'chip' | 'box';
  size?: 'small' | 'medium' | 'large' | number;
  position?: 'absolute' | 'relative';
  absolutePosition?: {
    top?: string | number;
    right?: string | number;
    bottom?: string | number;
    left?: string | number;
  };
  sx?: any;
}

const ComingSoonTag: React.FC<ComingSoonTagProps> = ({
  variant = 'chip',
  size = 'small',
  position = 'relative',
  absolutePosition = {},
  sx = {}
}) => {
  // Helper function to generate CSS classes
  const getCssClasses = () => {
    const baseClass = styles.comingSoonTag;
    
    if (variant === 'chip') {
      return baseClass;
    }
    
    // Box variant classes
    let sizeClass = '';
    if (typeof size === 'string') {
      switch (size) {
        case 'small':
          sizeClass = styles.boxSmall;
          break;
        case 'medium':
          sizeClass = styles.boxMedium;
          break;
        case 'large':
          sizeClass = styles.boxLarge;
          break;
        default:
          sizeClass = styles.boxSmall;
      }
    } else if (typeof size === 'number') {
      // Find the closest size class
      const sizeClasses = Object.keys(styles).filter(key => key.startsWith('size'));
      const closestSize = sizeClasses.reduce((prev, curr) => {
        const prevSize = parseInt(prev.replace('size', ''));
        const currSize = parseInt(curr.replace('size', ''));
        return Math.abs(currSize - size) < Math.abs(prevSize - size) ? curr : prev;
      });
      sizeClass = styles[closestSize as keyof typeof styles];
    }
    
    return `${baseClass} ${styles.box} ${sizeClass}`;
  };

  // Default styles for chip variant (MyBookings page style)
  const getChipStyles = () => {
    if (typeof size === 'number') {
      // Custom numeric size - more reasonable font size calculation
      return {
        fontSize: `${Math.max(0.6, size * 0.035)}rem`,
        height: size,
        ...sx
      };
    }
    
    // Predefined sizes
    const predefinedSizes = {
      small: { fontSize: '0.7rem', height: 20 },
      medium: { fontSize: '0.8rem', height: 24 },
      large: { fontSize: '0.9rem', height: 28 }
    };
    
    return {
      ...predefinedSizes[size || 'small'],
      ...sx
    };
  };

  // Get positioning styles for absolute positioning
  const getPositionStyles = () => {
    if (position === 'absolute' && Object.keys(absolutePosition).length > 0) {
      return {
        position: 'absolute',
        ...absolutePosition
      };
    }
    return {};
  };



  if (variant === 'chip') {
    return (
      <Chip
        label="Coming Soon"
        size={typeof size === 'number' ? 'small' : (size === 'large' ? 'medium' : size)}
        color="secondary"
        className={getCssClasses()}
        sx={{
          ...getChipStyles(),
          ...getPositionStyles()
        }}
      />
    );
  }

  return (
    <Box
      className={getCssClasses()}
      sx={{
        ...sx,
        ...getPositionStyles()
      }}
    >
      Coming Soon
    </Box>
  );
};

export default ComingSoonTag;
